import { Box, Divider, Link, MenuItem } from "@mui/material";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { IRankingExtendedSearch } from "src/api/server/extended-search.types";
import { Typography } from "src/components-mui/base/typography/typography";
import { SelectMenu } from "src/components-mui/common/context-menu/select-menu";
import { ISortDirectionType } from "src/types/common";
import { ISelectedRanking } from "../types";
import { getVisibleRankings } from "./utils/get-visible-rankings";

type IDropdownSelectorProps = {
    rankings: ReadonlyArray<IRankingExtendedSearch>;
    selectedRanking: ISelectedRanking;
    relevanceRanking: IRankingExtendedSearch;
    selectedFiguresIds: ReadonlyArray<string>;
    isDisabled: boolean;
    onChange: (selectedRanking: ISelectedRanking) => void;
    onAdjustFigures: () => void;
};

export const RankingsDropdownSelector = ({
    rankings,
    selectedRanking,
    relevanceRanking,
    selectedFiguresIds = [],
    isDisabled,
    onChange,
    onAdjustFigures,
}: IDropdownSelectorProps) => {
    const router = useRouter();
    const { searchTerm } = router.query;
    const { t } = useTranslation("extended-search");

    const visibleRankings = getVisibleRankings(rankings, relevanceRanking, selectedFiguresIds);

    const selectedRankingData = visibleRankings.find(ranking => ranking.id === selectedRanking.id) ?? visibleRankings[0];
    const selectedRankingLabel = selectedRankingData.label;
    const isPlaceholderLabel = selectedRankingData.id === null;

    const handleOnChange = (id: string | null) => {
        const defaultSortDirectionForSelectedRanking: ISortDirectionType = visibleRankings.find(ranking => ranking.id === id)?.sortDirection ?? null;
        onChange({ id, sortDirection: defaultSortDirectionForSelectedRanking });
    };

    const shouldRelevanceRankingBeDisabled = (id: string | null) => relevanceRanking.id === id && !searchTerm;

    return (
        <SelectMenu
            id="search-rankings-dropdown-menu"
            buttonId="search-rankings-dropdown-button"
            label={
                <Box component="span" sx={{ display: "block", color: isPlaceholderLabel ? "gray.500" : undefined }}>
                    {selectedRankingLabel}
                </Box>
            }
            disabled={isDisabled}
            size="small"
            marginThreshold={null}
            menuMaxHeight={401}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
            {({ closeMenu }) => (
                <>
                    {visibleRankings.map(({ id, label }) => (
                        <MenuItem
                            key={id ?? label}
                            selected={selectedRanking.id === id}
                            onClick={() => {
                                if (shouldRelevanceRankingBeDisabled(id)) {
                                    return;
                                }
                                handleOnChange(id);
                                closeMenu();
                            }}
                            disabled={shouldRelevanceRankingBeDisabled(id)}
                            sx={{
                                maxWidth: 250,
                                minHeight: { xs: 40 },
                                fontSize: "sm",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                "&.Mui-selected": {
                                    backgroundColor: "gray.100",
                                },
                                "&:hover": {
                                    backgroundColor: "gray.100",
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: "gray.100",
                                },
                                "&.Mui-disabled": {
                                    pointerEvents: "auto",
                                    cursor: "not-allowed",
                                    "&:hover": {
                                        backgroundColor: "transparent",
                                    },
                                },
                            }}
                        >
                            {label}
                        </MenuItem>
                    ))}
                    <Divider sx={{ mt: 1, mb: 0, mr: 2, ml: 2 }} />
                    <Box sx={{ px: 2, pt: 1, pb: 1 }}>
                        <Typography
                            component="p"
                            sx={{
                                fontSize: "sm",
                                color: "gray.700",
                            }}
                        >
                            {t("rankings-dropdown.sort-more-question")}{" "}
                            <Link
                                component="button"
                                type="button"
                                underline="hover"
                                onClick={() => {
                                    closeMenu();
                                    onAdjustFigures();
                                }}
                            >
                                {t("rankings-dropdown.adjust-figures-cta")}
                            </Link>
                        </Typography>
                    </Box>
                </>
            )}
        </SelectMenu>
    );
};
